import { useEffect, useRef, useState } from "react";
import { getStageColor } from "../services/dataService";
import mapImage from "../../resources/map.jpg";
import mapSmallImage from "../../resources/map_small.jpg";
import type { Stage } from "../types";

interface MapComponentProps {
  stage?: Stage;
  interactive?: boolean;
  onCoordinatesChange?: (x: number, y: number) => void;
}

type Coords = {
  x: number;
  y: number;
};

export default function MapComponent({
  stage,
  interactive = false,
  onCoordinatesChange,
}: MapComponentProps) {
  if (!stage) {
    return DefaultMapComponent();
  }

  const [iconPosition, setIconPosition] = useState<null | Coords>(null);
  const [backgroundScroll, setBackgroundScroll] = useState<Coords>({
    x: 0,
    y: 0,
  });
  const [localDragging, setLocalDragging] = useState(false);
  const mapDisplayRef = useRef<HTMLDivElement>(null);

  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });
  const scrollStartRef = useRef({ x: 0, y: 0 });

  useEffect(() => {
    if (isDraggingRef.current) return;

    if (mapDisplayRef.current) {
      const boundingRect = mapDisplayRef.current.getBoundingClientRect();

      const { offsetX, offsetY } = stage;
      setBackgroundScroll({
        x: -offsetX + boundingRect.width / 2,
        y: -offsetY + boundingRect.height / 2,
      });

      setIconPosition({
        x: boundingRect.width / 2 - 16,
        y: boundingRect.height / 2 - 16,
      });
    }
  }, [stage]);

  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!interactive) return;
    e.preventDefault();
    isDraggingRef.current = true;
    setLocalDragging(true);
    dragStartRef.current = { x: e.clientX, y: e.clientY };
    scrollStartRef.current = { x: backgroundScroll.x, y: backgroundScroll.y };
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current || !interactive || !mapDisplayRef.current) return;
    e.preventDefault();
    const deltaX = e.clientX - dragStartRef.current.x;
    const deltaY = e.clientY - dragStartRef.current.y;

    const newScrollX = scrollStartRef.current.x + deltaX;
    const newScrollY = scrollStartRef.current.y + deltaY;

    setBackgroundScroll({ x: newScrollX, y: newScrollY });

    if (onCoordinatesChange) {
      const boundingRect = mapDisplayRef.current.getBoundingClientRect();
      const newOffsetX = Math.round(-newScrollX + boundingRect.width / 2);
      const newOffsetY = Math.round(-newScrollY + boundingRect.height / 2);
      onCoordinatesChange(newOffsetX, newOffsetY);
    }
  };

  const handleMouseUpOrLeave = () => {
    isDraggingRef.current = false;
    setLocalDragging(false);
  };

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!interactive || e.touches.length !== 1) return;
    isDraggingRef.current = true;
    setLocalDragging(true);
    const touch = e.touches[0];
    dragStartRef.current = { x: touch.clientX, y: touch.clientY };
    scrollStartRef.current = { x: backgroundScroll.x, y: backgroundScroll.y };
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDraggingRef.current || !interactive || !mapDisplayRef.current || e.touches.length !== 1)
      return;
    const touch = e.touches[0];
    const deltaX = touch.clientX - dragStartRef.current.x;
    const deltaY = touch.clientY - dragStartRef.current.y;

    const newScrollX = scrollStartRef.current.x + deltaX;
    const newScrollY = scrollStartRef.current.y + deltaY;

    setBackgroundScroll({ x: newScrollX, y: newScrollY });

    if (onCoordinatesChange) {
      const boundingRect = mapDisplayRef.current.getBoundingClientRect();
      const newOffsetX = Math.round(-newScrollX + boundingRect.width / 2);
      const newOffsetY = Math.round(-newScrollY + boundingRect.height / 2);
      onCoordinatesChange(newOffsetX, newOffsetY);
    }
  };

  return (
    <div
      ref={mapDisplayRef}
      className="relative w-full h-[600px] overflow-hidden"
      style={{
        backgroundImage: `url("${mapImage}")`,
        backgroundPosition: `${backgroundScroll.x}px ${backgroundScroll.y}px`,
        cursor: interactive ? (localDragging ? "grabbing" : "grab") : "default",
      }}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUpOrLeave}
      onMouseLeave={handleMouseUpOrLeave}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleMouseUpOrLeave}
    >
      {/* Show stage marker if stage is specified */}
      {stage && iconPosition && (
        <div
          className="absolute -translate-x-[25%] -translate-y-[85%] flex items-center justify-center"
          style={{
            width: 36,
            height: 36,
            left: iconPosition.x,
            top: iconPosition.y,
            zIndex: 10,
          }}
        >
          {/* Pulsing Ring */}
          <div
            className="absolute w-7 h-7 rounded-full animate-ping opacity-75"
            style={{
              backgroundColor: getStageColor(stage.id),
            }}
          />
          {/* Pushpin Emoji */}
          <span className="text-3xl relative z-10 select-none drop-shadow-[0_3px_5px_rgba(0,0,0,0.4)]">
            📌
          </span>
        </div>
      )}
    </div>
  );
}

function DefaultMapComponent() {
  return (
    <div className="relative w-full">
      <a href={mapImage} target="_blank" rel="noopener noreferrer">
        <img src={mapSmallImage} alt="Térkép" />
      </a>
    </div>
  );
}
